/**
 * Browser wiring for the shared foundation modules. The pure logic lives in
 * @mononoke/shared and is unit-tested without a DOM; this file is the only place
 * that touches real browser APIs.
 */
import {
  BootWaterfall,
  ErrorLedger,
  ReloadGuard,
  appVersion,
  safeProbe,
  type StorageLike,
} from '@mononoke/shared';
import { monitoringStatus, reportLedgerEntry } from './monitoring.js';

/** localStorage may be absent (SSR, hardened browsers). Never assume it exists. */
function storageOrNull(pick: () => Storage): StorageLike | null {
  try {
    const s = pick();
    return safeProbe(s) ? s : null;
  } catch {
    return null;
  }
}

const local = storageOrNull(() => window.localStorage);
const session = storageOrNull(() => window.sessionStorage);

/** Boot kind detection — the three variants that funnel into one init path. */
export function detectBootKind(): 'cold' | 'resume' | 'auth-redirect' {
  if (window.location.hash.includes('access_token') || window.location.search.includes('code=')) {
    return 'auth-redirect';
  }
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return nav?.type === 'back_forward' ? 'resume' : 'cold';
}

export const ledger = new ErrorLedger({
  ...(local ? { storage: local } : {}),
  appVersion: appVersion(),
  remoteStatus: monitoringStatus,
  onEntry: (entry) => {
    // Guardrail 10's two halves fire from ONE place. Wiring them separately is how
    // one of them quietly stops firing and nobody notices for a month.
    reportLedgerEntry(entry);
    if (import.meta.env.DEV) console.warn('[ledger]', entry.code, entry.source);
  },
});

export const waterfall = new BootWaterfall(detectBootKind(), local ? { storage: local } : {});

/** sessionStorage-backed: survives the reload it guards, dies with the tab. */
export const reloadGuard = new ReloadGuard(session ?? { getItem: () => null, setItem: () => { throw new Error('no session storage'); }, removeItem: () => undefined, key: () => null, length: 0 });

/** True when persistence is unavailable — the Diagnostics screen must say so. */
export const storageAvailable = { local: local !== null, session: session !== null };

/** The probed local StorageLike, for modules with their own keys (auth). Null when the
 * device has no working storage; callers fall back explicitly, never silently. */
export const localStore: StorageLike | null = local;
